import * as Dialog from '@radix-ui/react-dialog';
import { ComponentPropsWithoutRef, forwardRef, ReactNode, RefObject, type JSX } from 'react';
import { tv } from 'tailwind-variants';
import { Slottable } from '@radix-ui/react-slot';
import { IconDefinition } from '@fortawesome/pro-light-svg-icons';
import { VisuallyHidden } from '@radix-ui/react-visually-hidden';

import { Icon } from './icon.component';

import { ModalCloseButton } from '@/components/ui/modal-close-button/modal-close-button.component';
import { cn } from '@/utils/cn-tw-merge';

export type ModalVariant = 'default' | 'desktopSideRight';

const contentModal = tv({
  slots: {
    content: [
      'data-[state=open]:animate-content-show-mobile',
      'md:data-[state=open]:animate-content-show',
      'fixed',
      'md:rounded-2xl bg-white',
      'focus:outline-none z-[51]',
      'overflow-hidden',
      'flex flex-col',
      'bottom-0 left-0 right-0 max-h-[85dvh]',
      'md:top-[50%] md:bottom-auto md:left-[50%]',
      'md:right-auto md:translate-x-[-50%] md:translate-y-[-50%]',
      'md:w-[90vw] md:max-w-[22.5rem]',
    ],
  },
  variants: {
    variant: {
      desktopSideRight: {
        content: [
          'md:right-0 md:left-auto md:translate-x-0 md:rounded-none',
          'md:min-h-[100dvh] md:max-w-[22.5rem]',
        ],
      },
      default: {
        content: [],
      },
    },
  },
  defaultVariants: {
    variant: 'default',
  },
});

const ModalOverlay = forwardRef<
  React.ElementRef<typeof Dialog.Overlay>,
  ComponentPropsWithoutRef<typeof Dialog.Overlay>
>(({ className, ...props }, ref) => (
  <Dialog.Overlay
    className={cn(
      'bg-gray-600/40 md:backdrop-blur-[4px] transition-opacity data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 fixed inset-0 z-[50]',
      className,
    )}
    {...props}
    ref={ref}
  />
));

ModalOverlay.displayName = Dialog.Overlay.displayName;

interface ContentModalProps {
  title?: string;
  children: ReactNode;
  slotHeader?: JSX.Element;
  className?: string;
  autoFocusRef?: RefObject<HTMLElement | null>;
  removeFocus?: boolean;
  icon?: IconDefinition;
  iconStyle?: string;
  variant?: ModalVariant;
  overlayClassName?: string;
  ['data-testid']?: string;
}

export function ContentModal({
  title,
  children,
  slotHeader,
  className,
  autoFocusRef,
  removeFocus,
  icon,
  iconStyle,
  variant,
  overlayClassName,
  'data-testid': dataTestId,
}: ContentModalProps) {
  const { content } = contentModal({
    variant,
  });

  const iconClasses = iconStyle ? iconStyle : '';

  const handleAutoFocus = (event: Event) => {
    if (removeFocus) {
      return event.preventDefault();
    }

    if (autoFocusRef) {
      event.preventDefault();

      if (autoFocusRef.current) {
        autoFocusRef.current.focus();
      }
    }
  };

  return (
    <Dialog.Portal>
      <ModalOverlay className={overlayClassName} />
      <Dialog.Content
        className={content({ className })}
        onOpenAutoFocus={handleAutoFocus}
        aria-describedby={undefined}
        data-testid={dataTestId}
      >
        {!slotHeader
          ? (
              <div className="flex justify-between gap-[10px] p-6 px-5 pb-0 items-center ">
                <Dialog.Title asChild className="self-start">
                  <div className="flex items-center gap-[10px]">
                    {icon && <Icon icon={icon} size="xl" className={iconClasses} />}

                    {title && <span className="font-bold text-2xl text-gray-900 text-nowrap">{title}</span>}
                  </div>
                </Dialog.Title>

                <Dialog.Close asChild className="self-center">
                  <ModalCloseButton skipInitialFocus data-testid="close-modal-button--content-modal" />
                </Dialog.Close>
              </div>
            )
          : (
              <Slottable>{slotHeader}</Slottable>
            )}

        {slotHeader && (
          <VisuallyHidden>
            <Dialog.Title>{title || 'Modal'}</Dialog.Title>
          </VisuallyHidden>
        )}

        <div className="flex-1 min-h-0 overflow-y-auto">
          {children}
        </div>
      </Dialog.Content>
    </Dialog.Portal>
  );
}
